'use client';
import type { FormFieldBlock } from '@payloadcms/plugin-form-builder/types';

import { useRouter } from 'next/navigation';
import React, { useCallback, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import RichText from '../components/RichText';
import { Button } from '../components/ui/button';

import { getClientSideURL } from '@/utilities/getURL';
import { fields } from '../components/FieldForm/fields';
import { FormBlock as FormBlockType, Form } from '@/payload-types';
import {
  PayloadRecaptcha,
  type RecaptchaHandle,
} from '@onclicks/recaptcha/components/PayloadRecaptcha';

export const FormBlock: React.FC<FormBlockType> = (props) => {
  const { form: formFromProps } = props;

  const isFormObject =
    typeof formFromProps === 'object' && formFromProps !== null;
  const form = isFormObject
    ? (formFromProps as Form & { requireRecaptcha?: boolean })
    : null;

  const formID = form?.id;
  const confirmationMessage = form?.confirmationMessage;
  const confirmationType = form?.confirmationType;
  const redirect = form?.redirect;
  const submitButtonLabel = form?.submitButtonLabel;
  const requireRecaptcha = form?.requireRecaptcha;

  const recaptcha = useRef<RecaptchaHandle>(null);

  const formMethods = useForm({
    defaultValues: form?.fields as unknown as FormFieldBlock[],
  });
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods;

  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState<boolean>();
  const [error, setError] = useState<
    { message: string; status?: string } | undefined
  >();
  const router = useRouter();

  const onSubmit = useCallback(
    (data: FormFieldBlock[]) => {
      let loadingTimerID: ReturnType<typeof setTimeout>;
      const submitForm = async () => {
        setError(undefined);

        const captchaValue = recaptcha.current
          ? recaptcha.current.getValue()
          : undefined;

        if (recaptcha && !captchaValue && requireRecaptcha) {
          setIsLoading(false);
          setError({
            message: 'Veuillez valider le captcha.',
            status: '400',
          });
          return;
        }

        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }));

        // delay loading indicator by 1s
        loadingTimerID = setTimeout(() => {
          setIsLoading(true);
        }, 1000);

        try {
          const req = await fetch(
            `${getClientSideURL()}/api/form-submissions`,
            {
              body: JSON.stringify({
                form: formID,
                submissionData: dataToSend,
                recaptcha: captchaValue,
              }),
              headers: {
                'Content-Type': 'application/json',
              },
              method: 'POST',
            },
          );

          const res = await req.json();

          clearTimeout(loadingTimerID);

          if (req.status >= 400) {
            setIsLoading(false);

            setError({
              message:
                res.errors?.[0]?.message ||
                "Une erreur s'est produite, Veuillez réessayer plus tard.",
              status: res.status,
            });

            return;
          }

          setIsLoading(false);
          setHasSubmitted(true);
          recaptcha.current?.reset?.();

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect;

            const redirectUrl = url;

            if (redirectUrl) router.push(redirectUrl);
          }
        } catch (err) {
          console.warn(err);
          setIsLoading(false);
          setError({
            message: "Une erreur s'est produire, Veuillez réessayer plus tard.",
          });
          recaptcha.current?.reset?.();
        }
      };

      void submitForm();
    },
    [router, formID, redirect, confirmationType, requireRecaptcha],
  );

  return (
    <div className="bottom-12 mx-auto w-full max-w-xl self-end xsmall:py-12 lg:sticky lg:mr-0 lg:max-w-lg px-4 lg:px-8 lg:py-0">
      <FormProvider {...formMethods}>
        {!isLoading &&
          hasSubmitted &&
          confirmationType === 'message' &&
          confirmationMessage && <RichText data={confirmationMessage} />}
        {isLoading && (
          <div className="mb-4 p-3 border rounded-lg bg-(--primary)/5 border-(--primary)/20 text-(--primary) flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-(--primary) border-t-transparent rounded-full animate-spin"></div>
            Chargement, veuillez patienter...
          </div>
        )}
        {!isLoading && error && (
          <div className="mb-4 p-3 border rounded-lg bg-red-50  border-red-200 text-red-600 ">
            {error.status ? `${error.status}: ` : ''}
            {error.message}
          </div>
        )}
        {!hasSubmitted && (
          <form
            id={formID?.toString()}
            onSubmit={(event) => {
              void handleSubmit(onSubmit)(event);
            }}
            className=""
          >
            <div className="flex flex-col gap-y-5">
              {formFromProps &&
                form?.fields &&
                form?.fields?.map((field, index) => {
                  const Field: React.FC<any> =
                    fields?.[field.blockType as keyof typeof fields];
                  if (Field) {
                    return (
                      <div key={index}>
                        <Field
                          form={formFromProps}
                          {...field}
                          {...formMethods}
                          control={control}
                          errors={errors}
                          register={register}
                        />
                      </div>
                    );
                  }
                  return null;
                })}
            </div>
            <div className="flex flex-col items-end gap-6 mt-8">
              <PayloadRecaptcha
                ref={recaptcha}
                requireRecaptcha={requireRecaptcha}
                className="w-full sm:w-auto"
              />
              <Button form={formID?.toString()} type="submit" variant="blue">
                {submitButtonLabel}
              </Button>
            </div>
          </form>
        )}
      </FormProvider>
    </div>
  );
};
